/* components/patterns/kitchen/DrawerStackRenderer.jsx — хост drawer-стека Кухни.
   Рендерит ВЕРХНИЙ элемент стека в общий `Drawer` (level=1, с backdrop).
   Крошки — в слоте breadcrumbs шапки Drawer; имя сущности — в заголовке.
   Контракт: registry[type] → компонент-редактор. В S2 registry пуст →
   рендерится <EditorPlaceholder/>. S3/S4 заполняют registry (card/ttk/menu)
   БЕЗ изменения этого файла. Размер: card→md, ttk/menu→lg; на ≤720 → 100vw (Drawer).
   ХЕНДОФФ → frontend/pages/kitchen/DrawerStackRenderer.jsx. */

import { useUnsaved } from '../../../app/providers/UnsavedChangesProvider.jsx';
import { KitchenBreadcrumb, useKitchenEntityName } from './KitchenBreadcrumb.jsx';
import { Button } from '../../ui/Button.jsx';
import { Drawer } from '../../ui/Drawer.jsx';
import { Icon } from '../../ui/Icon.jsx';

  const EDITOR_TITLE = { card: 'Карточка сырья', ttk: 'ТТК', menu: 'Проект меню' };
  const EDITOR_SIZE = { card: 'md', ttk: 'lg', menu: 'lg' };

  function EditorPlaceholder({ type, id, onClose }) {
    return (
      <div className="editor-placeholder">
        <div className="editor-placeholder__glyph"><Icon name="edit" size={26} /></div>
        <h3 className="t-h3">Редактор подключается на этапе S3 / S4</h3>
        <p className="t-body text-dim">
          Здесь появится {EDITOR_TITLE[type] || 'редактор'} — drawer-редактор из общих
          view/edit-блоков. Механизм стека уже работает: адрес <code className="num">?edit=</code>,
          крошки, история браузера (назад/вперёд).
        </p>
        <dl className="editor-placeholder__meta">
          <div className="editor-placeholder__pair"><dt className="t-caption">Тип</dt><dd className="num">{type}</dd></div>
          <div className="editor-placeholder__pair"><dt className="t-caption">Идентификатор</dt><dd className="num">{String(id)}</dd></div>
        </dl>
        <Button variant="secondary" iconLeft="close" onClick={onClose}>Закрыть</Button>
      </div>
    );
  }

  function DrawerStackRenderer({ stack = [], registry = {}, onClose, onPopTo, onRoot, resolveName }) {
    const top = stack.length ? stack[stack.length - 1] : null;
    // P1-25: resolveName (опц.) пробрасывается в хук и крошки; по умолчанию хук берёт боевой resolveKitchenEntityName.
    const name = useKitchenEntityName(top ? top.type : null, top ? top.id : null, resolveName);
    // сторож несохранённых изменений: закрытие/навигация по крошкам спрашивает подтверждение
    const unsaved = useUnsaved ? useUnsaved() : null;
    const guarded = (fn) => (...args) => {
      if (unsaved && unsaved.guard) unsaved.guard(() => fn(...args));
      else fn(...args);
    };
    const gClose = guarded(onClose), gPopTo = guarded(onPopTo), gRoot = guarded(onRoot);
    if (!top) return null;

    const Editor = registry[top.type];
    const title = name || EDITOR_TITLE[top.type] || 'Редактор';

    return (
      <Drawer open level={1} size={EDITOR_SIZE[top.type] || 'md'}
        title={title}
        hideTitle={!!Editor}
        breadcrumbs={<KitchenBreadcrumb stack={stack} onRoot={gRoot} onPopTo={gPopTo} resolveName={resolveName} />}
        onClose={gClose}>
        {Editor
          ? <Editor type={top.type} id={top.id} stack={stack} onClose={gClose} />
          : <EditorPlaceholder type={top.type} id={top.id} onClose={gClose} />}
      </Drawer>
    );
  }

export { DrawerStackRenderer };
export { EditorPlaceholder };
